import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import CustomBreadcrumbs from '../CustomBreadcrumbs/CustomBreadcrumbs'
import Layout from '../Layout/Layout'
import { StructureType, Structure } from '../../types'
import {
  getCompanyStructureTypes,
  getCompanyStructureByType,
  updateCompanyPayrollConfig,
  getCompany,
} from '../../services/CompaniesService'
import { useAuth0 } from '@auth0/auth0-react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import Grid from '@mui/material/Grid'
import { useParams } from 'react-router-dom'
import ConfirmationModal from '../Modals/ConfirmationModal/ConfirmationModal'
import { useEmpresaStore } from '../../store/empresaStore'
import { shallow } from 'zustand/shallow'

const EmpresaConfigurarNomina: React.FC = () => {
  const { t } = useTranslation('empresaParametros')
  const { empresaId } = useParams<{ empresaId: string }>()
  const { selectedEmpresa, setSelectedEmpresa } = useEmpresaStore(
    state => ({
      selectedEmpresa: state.selectedEmpresa,
      setSelectedEmpresa: state.setSelectedEmpresa,
    }),
    shallow,
  )

  const [openModal, setOpenModal] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const [initialValues, setInitialValues] = useState({
    structureType: '',
    structure: '',
    requiresNoveltyApproval: false,
  })
  const previousStructureType = useRef('')

  const formik = useFormik({
    initialValues: {
      structureType: '',
      structure: '',
      requiresNoveltyApproval: false,
    },
    validationSchema: Yup.object({
      structureType: Yup.string(),
      structure: Yup.string().when('structureType', {
        is: (val: string) => val && val.length > 0,
        then: schema => schema.required(t('nomina.validation.structureRequired')),
        otherwise: schema => schema,
      }),
    }),
    onSubmit: async values => {
      if (!empresaId) return
      const token = await getAccessTokenSilently()
      try {
        await updateCompanyPayrollConfig(token, empresaId, {
          structureTypeId: values.structureType || '',
          structureId: values.structure || '',
          requiresNoveltyApproval: values.requiresNoveltyApproval,
        })
        // Actualizar initialValues después de guardar exitosamente
        setInitialValues({
          structureType: values.structureType,
          structure: values.structure,
          requiresNoveltyApproval: values.requiresNoveltyApproval,
        })
        // Actualizar el store para que otras vistas reflejen el cambio
        if (selectedEmpresa) {
          setSelectedEmpresa({
            ...selectedEmpresa,
            payrollConfig: {
              structureTypeId: values.structureType || '',
              structureId: values.structure || '',
              requiresNoveltyApproval: values.requiresNoveltyApproval,
            },
          })
        }
      } catch (error) {
        console.error('Error updating company payroll config:', error)
      }
    },
  })

  const handleCloseModal = () => {
    setOpenModal(false)
    setConfirmAction(null)
  }

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction()
    }
    handleCloseModal()
  }

  // Función para limpiar el formulario
  const handleClear = () => {
    formik.setValues({
      structureType: '',
      structure: '',
      requiresNoveltyApproval: false,
    })
    setModalMessage(t('nomina.messages.clearConfirm'))
    setConfirmAction(() => () => formik.submitForm())
    setOpenModal(true)
  }

  const handleSaveClick = () => {
    const structureChanged =
      formik.values.structureType !== initialValues.structureType ||
      formik.values.structure !== initialValues.structure
    const approvalChanged =
      formik.values.requiresNoveltyApproval !== initialValues.requiresNoveltyApproval

    let message = ''

    if (structureChanged && approvalChanged) {
      // Ambos cambiaron
      message = t('nomina.messages.bothChanged')
    } else if (structureChanged) {
      // Solo cambiaron los filtros
      if (formik.values.structureType && formik.values.structure) {
        message = t('nomina.messages.structureChangedWithFilters')
      } else {
        message = t('nomina.messages.structureChangedNoFilters')
      }
    } else if (approvalChanged) {
      // Solo cambió el approval
      if (formik.values.requiresNoveltyApproval) {
        message = t('nomina.messages.approvalEnabled')
      } else {
        message = t('nomina.messages.approvalDisabled')
      }
    }

    setModalMessage(message)
    setConfirmAction(() => () => formik.submitForm())
    setOpenModal(true)
  }

  const [structureTypes, setStructureTypes] = useState<StructureType[]>([])
  const [structures, setStructures] = useState<Structure[]>([])
  const [loadingTypes, setLoadingTypes] = useState(false)
  const [loadingStructures, setLoadingStructures] = useState(false)
  const { getAccessTokenSilently } = useAuth0()
  useEffect(() => {
    if (!empresaId) return
    const fetchCompanyConfig = async () => {
      const token = await getAccessTokenSilently()
      try {
        const response = await getCompany(token, empresaId)
        // Access nested properties safely
        const data = response.data
        // Check for direct property or nested inside another object (adjust based on actual API response)
        const payrollConfig = data.payrollConfig || data.company?.payrollConfig

        if (payrollConfig) {
          const configValues = {
            structureType: payrollConfig.structureTypeId || '',
            structure: payrollConfig.structureId || '',
            requiresNoveltyApproval: payrollConfig.requiresNoveltyApproval || false,
          }
          setInitialValues(configValues)
          formik.setValues(configValues)
          // Actualizar el ref para que no se dispare el efecto de limpieza
          previousStructureType.current = configValues.structureType
        }
      } catch (error) {
        console.error('Error fetching company config:', error)
      }
    }
    fetchCompanyConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, getAccessTokenSilently])

  const links: any[] = [
    { id: '1', value: t('breadcrumbLinks.empresas'), isLink: true, to: '/empresas' },
    { id: '2', value: t('breadcrumbLinks.empresa'), isLink: true, to: `/empresas/empresa/${empresaId}` },
    {
      id: '3',
      value: t('breadcrumbLinks.parametros'),
      isLink: true,
      to: `/empresas/empresa/${empresaId}/parametros`,
    },
    { id: '4', value: t('nomina.breadcrumbLabel'), isLink: false },
  ]
  useEffect(() => {
    if (!empresaId) return

    async function fetchCompanyStructureTypes() {
      setLoadingTypes(true)
      const token = await getAccessTokenSilently()
      // @ts-ignore
      getCompanyStructureTypes(token, empresaId)
        .then(res => {
          setStructureTypes(res.data)
        })
        .finally(() => setLoadingTypes(false))
    }
    fetchCompanyStructureTypes()
  }, [empresaId, getAccessTokenSilently])

  useEffect(() => {
    // Limpiar el campo structure cuando cambia structureType manualmente
    if (
      previousStructureType.current !== '' &&
      previousStructureType.current !== formik.values.structureType
    ) {
      if (formik.values.structure) {
        formik.setFieldValue('structure', '')
      }
    }
    previousStructureType.current = formik.values.structureType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.structureType])

  useEffect(() => {
    async function fetchStructures() {
      if (formik.values.structureType) {
        setLoadingStructures(true)
        const token = await getAccessTokenSilently()
        getCompanyStructureByType(token, empresaId as string, formik.values.structureType)
          .then(res => {
            setStructures(res.data)
          })
          .finally(() => setLoadingStructures(false))
      } else {
        setStructures([])
      }
    }
    fetchStructures()
  }, [formik.values.structureType, empresaId, getAccessTokenSilently])

  return (
    <Grid
      bgcolor={'white'}
      container
      marginTop="12px"
      paddingY="24px"
      paddingX={6}
      borderRadius="6px"
    >
      <Box component="form" width={600} autoComplete="off" onSubmit={formik.handleSubmit}>
        {/* Sección: Nómina */}
        <Box mb={4}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'left' }}>
            {t('nomina.sections.payroll')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box
            display="flex"
            flexDirection="column"
            justifyContent={'flex-start '}
            alignItems={'self-start'}
            maxWidth={300}
          >
            <InputLabel sx={{ color: 'black', mb: 1 }} htmlFor="structure-type-select">
              {t('nomina.fields.structureType')}
            </InputLabel>

            <TextField
              id="structure-type-select"
              select
              error={Boolean(formik.touched.structureType && formik.errors.structureType)}
              fullWidth
              helperText={formik.touched.structureType && formik.errors.structureType}
              name="structureType"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.structureType}
              variant="filled"
              placeholder={t('nomina.fields.structureType')}
              InputProps={{
                disableUnderline: true,
                sx: {
                  maxHeight: '300px',
                  width: '100%',
                  borderRadius: '6px',
                  '.MuiInputBase-input': {
                    paddingBottom: '16px',
                    paddingTop: '16px',
                  },
                },
              }}
              disabled={loadingTypes}
            >
              {loadingTypes ? (
                <MenuItem value="">
                  <CircularProgress size={20} /> {t('nomina.fields.loadingTypes')}
                </MenuItem>
              ) : (
                structureTypes.map(type => (
                  <MenuItem key={type.id} value={String(type.id)}>
                    {type.description}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            justifyContent={'flex-start '}
            alignItems={'self-start'}
            maxWidth={300}
          >
            <InputLabel sx={{ color: 'black', mt: 3, mb: 1 }} htmlFor="structure-select">
              {t('nomina.fields.structure')}
            </InputLabel>
            <TextField
              id="structure-select"
              select
              error={Boolean(formik.touched.structure && formik.errors.structure)}
              fullWidth
              helperText={formik.touched.structure && formik.errors.structure}
              name="structure"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.structure}
              variant="filled"
              placeholder={t('nomina.fields.structure')}
              InputProps={{
                disableUnderline: true,
                sx: {
                  borderRadius: '6px',
                  '.MuiInputBase-input': {
                    paddingBottom: '16px',
                    paddingTop: '16px',
                  },
                },
              }}
              disabled={loadingStructures || !formik.values.structureType}
            >
              {loadingStructures ? (
                <MenuItem value="">
                  <CircularProgress size={20} /> {t('nomina.fields.loadingStructures')}
                </MenuItem>
              ) : (
                structures.map(structure => (
                  <MenuItem key={structure.id} value={String(structure.id)}>
                    {structure.description}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Box>
        </Box>

        {/* Sección: Procesamiento */}
        <Box mb={4}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'left' }}>
            {t('nomina.sections.processing')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box display="flex" alignItems="flex-start">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.requiresNoveltyApproval}
                  onChange={formik.handleChange}
                  name="requiresNoveltyApproval"
                  color="primary"
                />
              }
              label={t('nomina.requiresApproval')}
              sx={{ m: 0 }}
            />
          </Box>
        </Box>

        <Box display="flex" alignItems="flex-start" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={
              loadingTypes ||
              loadingStructures ||
              (formik.values.structureType === '' && formik.values.structure === '')
            }
          >
            {t('nomina.actions.clear')}
          </Button>

          <Button
            type="button"
            onClick={handleSaveClick}
            variant="contained"
            color="primary"
            disabled={
              loadingTypes ||
              loadingStructures ||
              (formik.values.structureType !== '' && formik.values.structure === '') ||
              (formik.values.structureType === initialValues.structureType &&
                formik.values.structure === initialValues.structure &&
                formik.values.requiresNoveltyApproval ===
                  initialValues.requiresNoveltyApproval)
            }
          >
            {t('nomina.actions.save')}
          </Button>
        </Box>
      </Box>
      <ConfirmationModal
        open={openModal}
        onClose={handleCloseModal}
        saveForm={handleConfirm}
        isDirty={false}
        clickCancelar={false}
        handleDeshacerCancelar={() => {}}
        guardar={true}
        guardarMessage={modalMessage}
        onOpen={() => {}}
      />
    </Grid>
  )
}

export default EmpresaConfigurarNomina
